import { ChakraProvider } from "@chakra-ui/react";
import type { AppProps } from "next/app";
import { Analytics } from "@vercel/analytics/react";
import type { Session } from "next-auth";
import { SessionProvider } from "next-auth/react";
import { Provider as RWBProvider } from "react-wrap-balancer";
import cx from "classnames";
import localFont from "next/font/local";
import { Inter } from "next/font/google";
import store from "../stores/store";
import { Provider } from "react-redux";
import { useEffect } from "react";
import { ThemeProvider } from "next-themes";
import { Toaster, FlyoutProvider } from "@/components/shared/atoms/toaster";
import systemWithTheme from "../theme";
import Layout from "@/components/layout";

// Extend the Window interface to include 'rt'
declare global {
  interface Window {
    rt?: {
      debug: {
        reduxStore: typeof store;
        reduxDispatch: typeof store.dispatch;
      };
    };
  }
}

const sfPro = localFont({
  src: "../styles/SF-Pro-Display-Medium.otf",
  variable: "--font-sf",
  display: "swap", // Recommended
});

const inter = Inter({
  variable: "--font-inter",
  subsets: ["latin"],
});

export default function MyApp({
  Component,
  pageProps: { session, meta, hideLogin, ...pageProps },
}: AppProps<{
  session: Session;
  meta?: Record<string, unknown>;
  hideLogin?: boolean;
}>) {
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      // Attach the Redux store and dispatch to the window in development mode
      window.rt = {
        debug: { reduxStore: store, reduxDispatch: store.dispatch },
      };
    }
  }, []);

  return (
    <ChakraProvider value={systemWithTheme}>
      <Provider store={store}>
        <SessionProvider
          session={session}
          refetchInterval={0} // Disable automatic refetching
          refetchOnWindowFocus={false} // Disable refetch on window focus
        >
          <RWBProvider>
            <div
              className={cx(sfPro.variable, inter.variable, "flex", "flex-col")}
            >
              <ThemeProvider attribute='class' disableTransitionOnChange>
                <FlyoutProvider>
                  <Layout meta={meta} hideLogin={hideLogin}>
                    {(layoutProps) => (
                      <Component {...pageProps} {...layoutProps} />
                    )}
                  </Layout>
                  <Toaster />
                </FlyoutProvider>
              </ThemeProvider>
            </div>
          </RWBProvider>
          <Analytics />
        </SessionProvider>
      </Provider>
    </ChakraProvider>
  );
}
